/**
 * Axios Instance Configuration for REST API
 * BFF (Backend For Frontend) Pattern - Cookie + CSRF Authentication
 */
import axios from 'axios'
import { toast } from 'vue-sonner'

const api = axios.create({
    baseURL: '/',  // Same-origin (Admin 자체 API 컨트롤러로 요청, Admin.Api와 무관)
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
    },
    withCredentials: true,  // Send cookies
    timeout: 30000,
})

// Store references (initialized from main.js)
let authStoreRef = null
let routerRef = null
let i18nRef = null

export function setAuthStore(store) {
    authStoreRef = store
}

export function setRouter(router) {
    routerRef = router
}

export function setI18n(i18n) {
    i18nRef = i18n
}

// Helper to get translation
function t(key, fallback) {
    if (i18nRef?.global?.t) {
        return i18nRef.global.t(key)
    }
    return fallback
}

// Request interceptor - Add CSRF token for non-GET requests
api.interceptors.request.use(
    (config) => {
        console.log(`[API] Request: ${config.method?.toUpperCase()} ${config.url}`)

        // GET requests don't need CSRF token
        if (config.method === 'get') {
            return config
        }

        // Add Antiforgery token for POST/PUT/DELETE
        const token = authStoreRef?.antiforgeryToken
        if (token) {
            config.headers['RequestVerificationToken'] = token
        } else {
            console.warn('[API] ⚠️ No CSRF token available for', config.method?.toUpperCase(), config.url)
        }

        return config
    },
    (error) => {
        console.error('[API] Request error:', error)
        return Promise.reject(error)
    }
)

// Response interceptor - Handle errors and redirects
api.interceptors.response.use(
    (response) => {
        console.log(`[API] Response: ${response.status} ${response.config.url}`)
        const data = response.data

        // Handle redirect response
        if (data?.redirectUrl) {
            console.log(`[API] Redirect to: ${data.redirectUrl}`)
            if (data.redirectUrl.startsWith('http')) {
                window.location.href = data.redirectUrl
            } else if (routerRef) {
                routerRef.push(data.redirectUrl)
            }
        }

        return response
    },
    (error) => {
        const status = error.response?.status
        const data = error.response?.data

        console.error(`[API] Error: ${status} ${error.config?.url}`, data)

        // Network error (no response)
        if (!error.response) {
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                toast.error(t('messages.timeout', '요청 시간이 초과되었습니다'), {
                    description: t('messages.timeoutDescription', '네트워크 연결을 확인하고 다시 시도해주세요')
                })
            } else {
                toast.error(t('messages.networkError', '네트워크 오류가 발생했습니다'), {
                    description: t('messages.networkErrorDescription', '인터넷 연결을 확인해주세요')
                })
            }
            return Promise.reject({ status: 0, title: 'Network Error', detail: error.message })
        }

        // Server error (5xx)
        if (status >= 500) {
            toast.error(t('messages.serverError', '서버 오류가 발생했습니다'), {
                description: t('messages.serverErrorDescription', '잠시 후 다시 시도해주세요')
            })
        }

        // 401 Unauthorized - Redirect to login
        if (status === 401) {
            const currentPath = window.location.pathname
            const inLoginFlow =
                currentPath.toLowerCase().includes('/account/login') ||
                currentPath.toLowerCase().includes('/signin-oidc')

            // 무한 새로고침 가드: 짧은 시간 내 401 redirect 반복 감지 시 중단.
            // 원인 추정 — 인증 쿠키 디코드 실패 / chunked cookie 누락 / antiforgery
            // 정합성 깨짐. 무한 reload 대신 사용자에게 가이드 토스트.
            const now = Date.now()
            const lastRedirectAt = parseInt(sessionStorage.getItem('lastAuthRedirectAt') || '0', 10)
            const redirectCount = parseInt(sessionStorage.getItem('authRedirectCount') || '0', 10)
            const withinWindow = now - lastRedirectAt < 10_000  // 10s 윈도우

            if (withinWindow && redirectCount >= 2) {
                // 10초 내 3회째 401 → 루프로 간주
                sessionStorage.removeItem('lastAuthRedirectAt')
                sessionStorage.removeItem('authRedirectCount')
                toast.error(t('messages.authLoop', '인증 루프가 감지되었습니다'), {
                    description: t('messages.authLoopDescription', '브라우저 쿠키를 삭제하거나 다른 브라우저로 다시 시도해 주세요'),
                    duration: 15_000
                })
                if (authStoreRef) authStoreRef.clearAuth()
                return Promise.reject(error)
            }

            if (!inLoginFlow) {
                toast.warning(t('messages.sessionExpired', '세션이 만료되었습니다'), {
                    description: t('messages.sessionExpiredDescription', '다시 로그인해주세요')
                })
            }

            sessionStorage.setItem('lastAuthRedirectAt', String(now))
            sessionStorage.setItem('authRedirectCount', String(withinWindow ? redirectCount + 1 : 1))

            if (authStoreRef) {
                authStoreRef.clearAuth()
            }

            // Redirect to AdminUI/Account/Login to trigger OIDC authentication
            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
            window.location.href = `/AdminUI/Account/Login?returnUrl=${returnUrl}`
            return Promise.reject(error)
        }

        // 403 Forbidden — 인증은 됐지만 정책 위반. 토스트는 짧은 시간 윈도우에 1회만.
        // (페이지 mount 시 여러 API 가 동시에 403을 받으면 토스트가 폭주하는 걸 방지)
        if (status === 403) {
            const now = Date.now()
            const lastForbiddenToastAt = parseInt(sessionStorage.getItem('lastForbiddenToastAt') || '0', 10)
            if (now - lastForbiddenToastAt > 5_000) {
                toast.error(t('messages.unauthorized', '권한이 없습니다'))
                sessionStorage.setItem('lastForbiddenToastAt', String(now))
            }
        }

        // Return structured error info
        const errorInfo = {
            status: status,
            title: data?.title || t('messages.error', '오류'),
            detail: data?.detail || data?.message || t('messages.errorOccurred', '오류가 발생했습니다'),
            code: data?.code || 'UNKNOWN_ERROR',
            errors: data?.errors,
            original: error
        }

        return Promise.reject(errorInfo)
    }
)

export default api
